import React, { useEffect, useState } from 'react';
import { Menu, Sparkles, Sun, Moon } from 'lucide-react';
import { Logo } from './Logo';
import { NavLinks } from './NavLinks';
import { MobileMenu } from './MobileMenu';
import { trackButtonClick, trackCustomEvent } from '../../firebaseConfig';
import { useAnalytics } from '../../lib/AnalyticsContext';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

// Theme type for better type safety
type Theme = 'light' | 'dark';

interface NavbarProps {
  onAnimationStateChange?: (state: string) => void;
}

// Local storage key for theme
const THEME_STORAGE_KEY = 'mocktrail-theme-preference';

export function Navbar({ onAnimationStateChange }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false); // Default to light theme
  const [animationState, setAnimationState] = useState("initial"); // "initial", "centered", "expanded"
  const { trackUserAction } = useAnalytics();
  const { trackButtonClick: trackAnalyticsButton, trackConversion } = useAnalyticsTracking();

  // Load theme from localStorage and apply it on initial render
  useEffect(() => {
    // Get saved theme preference from localStorage
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    
    // If no saved preference, default to light theme
    const preferredTheme = savedTheme || 'light';
    
    // Apply the theme
    setIsDark(preferredTheme === 'dark');
    document.documentElement.classList.toggle('dark', preferredTheme === 'dark');
    
    // Stage 1: Show centered logo
    const centeredLogoTimeout = setTimeout(() => {
      setAnimationState("centered");
      if (onAnimationStateChange) onAnimationStateChange("centered");
    }, 300);
    
    // Stage 2: Expand navbar after delay
    const expandTimeout = setTimeout(() => {
      setAnimationState("expanded");
      if (onAnimationStateChange) onAnimationStateChange("expanded");
    }, 1500);
    
    return () => {
      clearTimeout(centeredLogoTimeout);
      clearTimeout(expandTimeout);
    };
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    
    // Apply theme to DOM
    document.documentElement.classList.toggle('dark', newTheme);
    
    // Save theme preference to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, newTheme ? 'dark' : 'light');
    
    // Analytics tracking
    trackAnalyticsButton('Toggle Theme', 'Navbar', {
      button_type: 'utility',
      button_position: 'navbar_theme',
      theme_from: isDark ? 'dark' : 'light',
      theme_to: newTheme ? 'dark' : 'light'
    });
    
    // Track theme toggle event
    trackButtonClick('Theme Toggle', 'Navbar');
    trackUserAction('theme_change', {
      new_theme: newTheme ? 'dark' : 'light',
      component: 'Navbar'
    });
  };

  const handleTryMockTrail = () => {
    // Analytics tracking
    trackAnalyticsButton('Try MockTrail', 'Navbar', {
      button_type: 'primary_cta',
      button_position: 'navbar_cta',
      conversion_intent: 'sign_up'
    });
    
    // Track conversion
    trackConversion('sign_up', {
      source: 'navbar',
      button_name: 'Try MockTrail'
    });
    
    // Track try mocktrail button click
    trackButtonClick('Try MockTrail', 'Navbar');
    trackUserAction('conversion_action', {
      action: 'try_mocktrail',
      location: 'Navbar'
    });
    
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    // Open the app page in a new tab
    window.open(`${platformUrl}/login?utm_source=landing_page&utm_medium=navbar&utm_campaign=try_mocktrail`, '_blank');
  };

  const handleMenuToggle = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    
    // Analytics tracking
    trackAnalyticsButton('Mobile Menu Toggle', 'Navbar', {
      button_type: 'utility',
      button_position: 'navbar_mobile',
      menu_action: newMenuState ? 'open' : 'close'
    });
    
    // Track mobile menu toggle
    trackCustomEvent('mobile_menu_toggle', {
      state: newMenuState ? 'open' : 'closed'
    });
  };

  const getNavbarClasses = () => {
    if (animationState === "initial") {
      return "opacity-0 -translate-y-10 w-0";
    } else if (animationState === "centered") {
      return "opacity-100 translate-y-0 w-[210px] transition-all duration-800 ease-out";
    } else {
      // Super smooth expansion with longer duration and sophisticated easing
      return "opacity-100 translate-y-0 w-[calc(100%-2rem)] max-w-7xl transition-all duration-[1800ms] ease-[cubic-bezier(0.16,1,0.3,1)]"; 
    }
  };

  return (
    <div 
      className={`
        fixed z-50 top-6 left-1/2 -translate-x-1/2
        ${getNavbarClasses()}
      `}
    >
      <nav className="navbar-glass rounded-2xl border border-border-light dark:border-border-dark overflow-hidden">
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Logo isExpanded={animationState === "expanded"} isCentered={animationState === "centered"} />
            
            {/* Center Nav Links */}
            <div className={`
              hidden lg:block transition-all duration-700 ease-out
              ${animationState === "expanded" ? "opacity-100 translate-y-0 delay-500" : "opacity-0 -translate-y-5 absolute invisible"}
            `}>
              <NavLinks />
            </div>

            {/* Right Side Actions */}
            <div className={`
              flex items-center gap-4 transition-all duration-700 ease-out
              ${animationState === "expanded" ? "opacity-100 translate-y-0 delay-700" : "opacity-0 -translate-y-5 absolute invisible"}
            `}>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-full bg-surface-light/50 dark:bg-surface-dark/50 border border-border-light dark:border-border-dark hover:bg-primary-light/10 dark:hover:bg-primary-dark/10 transition-all duration-300"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <Moon className="w-5 h-5 text-primary-dark" />
                ) : (
                  <Sun className="w-5 h-5 text-primary-light" />
                )}
              </button>

              <div className="hidden lg:flex items-center gap-6">
                <button 
                  onClick={handleTryMockTrail}
                  className="btn-primary inline-flex items-center gap-2 px-8 w-full sm:w-auto relative overflow-hidden group"
                >
                  {/* Button shine effect */}
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>

                  Sign Up
                  <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="lg:hidden p-2.5 hover:bg-surface-light/50 dark:hover:bg-surface-dark/50 rounded-lg transition-all duration-300 hover:scale-105"
                onClick={handleMenuToggle}
                aria-label="Toggle menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <MobileMenu isOpen={isMenuOpen} />
      </nav>
    </div>
  );
}